// src/components/DiscoverySettingsModal.js

import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { useData } from '../context/DataContext';
import styles from './DiscoverySettingsModal.module.css'; // We will create this file next
import Spinner from './Spinner';

// Custom styles for react-select to match the dark theme
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#3a3a3a',
    borderColor: state.isFocused ? '#6f9aee' : '#555',
    boxShadow: state.isFocused ? '0 0 0 1px #6f9aee' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#6f9aee' : '#777' },
    cursor: 'pointer',
  }),
  menu: (provided) => ({ ...provided, backgroundColor: '#2d2d2d', zIndex: 10 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#4a4a4a'
      : state.isFocused
        ? '#3a3a3a'
        : '#2d2d2d',
    color: '#e0e0e0',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#4a4a4a' },
  }),
  multiValue: (styles) => ({ ...styles, backgroundColor: '#555' }),
  multiValueLabel: (styles) => ({ ...styles, color: '#e0e0e0' }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: '#e0e0e0',
    ':hover': { backgroundColor: '#f44336', color: 'white' },
  }),
  input: (provided) => ({ ...provided, color: '#e0e0e0' }),
  placeholder: (provided) => ({ ...provided, color: '#aaa' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

const DiscoverySettingsModal = () => {
  const {
    isDiscoverySettingsModalOpen,
    setIsDiscoverySettingsModalOpen,
    discoverySettings,
    discoverySettingsLoading,
    discoverySettingsError,
    fetchAllCountries,
    fetchDiscoverySettings,
    updateDiscoverySettings,
    generateDiscoveryKeywords,
  } = useData();

  // Local state for the form inputs
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]); // For the react-select component
  const [keywordsText, setKeywordsText] = useState(''); // For the textarea
  const [successMessage, setSuccessMessage] = useState('');

  // AI Generator States
  const [keywordCount, setKeywordCount] = useState(75);
  const [isGenerating, setIsGenerating] = useState(false);
  // This prevents the "wipeout" when the modal is open and global state fluctuates.
  const isInitialLoadDone = useRef(false);

  // Reset the initialization flag whenever the modal closes
  useEffect(() => {
    if (!isDiscoverySettingsModalOpen) {
      isInitialLoadDone.current = false;
    } else {
      // Fetch fresh data when opening
      fetchAllCountries().then((result) => {
        if (result.status) setCountryOptions(result.data);
      });
      fetchDiscoverySettings();
    }
  }, [isDiscoverySettingsModalOpen, fetchAllCountries, fetchDiscoverySettings]);

  // FIX 2: Controlled synchronization from DB to UI
  useEffect(() => {
    if (discoverySettings && !isInitialLoadDone.current) {
      console.debug(
        '[DiscoverySettingsModal] Syncing DB data to UI for the first time.',
      );

      // Set keywords from DB
      setKeywordsText(discoverySettings.description_keywords_text || '');

      // Set countries from DB
      if (countryOptions.length > 0) {
        const selected = (discoverySettings.excluded_countries || [])
          .map((code) => countryOptions.find((opt) => opt.value === code))
          .filter(Boolean);
        setSelectedCountries(selected);

        // Mark as done so we don't overwrite the user's manual typing later
        isInitialLoadDone.current = true;
      }
    }
  }, [discoverySettings, countryOptions]);

  const handleCloseRequest = () => {
    if (discoverySettingsLoading) return;
    setIsDiscoverySettingsModalOpen(false);
  };

  const handleSave = async () => {
    console.debug('[DiscoverySettingsModal] Save clicked.');
    setSuccessMessage(''); // Clear previous success message

    const payload = {
      excluded_countries: selectedCountries.map((opt) => opt.value), // Extract just the ISO codes
      description_keywords_text: keywordsText,
    };

    const result = await updateDiscoverySettings(payload);

    if (result.status) {
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000); // Hide after 3 seconds
    }
    // Error is handled and displayed by the context's `discoverySettingsError` state
  };

  const handleGenerateKeywords = async () => {
    if (keywordCount < 50 || keywordCount > 150) return;

    // FIX 3: Safety Confirmation
    if (keywordsText.trim().length > 0) {
      const proceed = window.confirm(
        'This will use AI to find new keywords and merge them with your current list. ' +
          'The entire list will be alphabetized. Do you want to proceed?',
      );
      if (!proceed) return;
    }

    setIsGenerating(true);
    setSuccessMessage('');

    const result = await generateDiscoveryKeywords(keywordCount);

    if (result.status && result.data) {
      // FIX 4: Smart Merge (Unique + Sorted)
      // 1. Get existing words (convert to lowercase for deduplication)
      const existingWords = keywordsText
        .split('\n')
        .map((w) => w.trim().toLowerCase())
        .filter(Boolean);

      // 2. Get new words from AI
      const newWords = result.data.map((w) => w.toLowerCase());

      // 3. Create a unique Set and Sort it
      const combinedUnique = Array.from(
        new Set([...existingWords, ...newWords]),
      ).sort();

      setKeywordsText(combinedUnique.join('\n'));
      setSuccessMessage(
        `✨ Added ${result.data.length} new keywords. Please review and click 'Update Settings' to save changes.`,
      );
    }
    setIsGenerating(false);
  };

  return (
    <Modal
      isOpen={isDiscoverySettingsModalOpen}
      onRequestClose={handleCloseRequest}
      className={styles.modalContent}
      overlayClassName={styles.modalOverlay}
      contentLabel="Discovery Settings"
      shouldCloseOnOverlayClick={!discoverySettingsLoading && !isGenerating}
      shouldCloseOnEsc={!discoverySettingsLoading && !isGenerating}
      ariaHideApp={false}
    >
      <h2>Discovery Settings</h2>

      {isGenerating && (
        <Spinner message="Please wait while the AI is generating the keywords ..." />
      )}
      {discoverySettingsLoading && !isGenerating && <Spinner />}
      {discoverySettingsError && (
        <div className={styles.modalError}>{discoverySettingsError}</div>
      )}
      {successMessage && (
        <div className={styles.modalSuccess}>{successMessage}</div>
      )}

      <div className={styles.modalBody}>
        <div className={styles.leftPanel}>
          <label htmlFor="country-select" className={styles.panelLabel}>
            Exclude Influencers from these Countries
          </label>
          <Select
            id="country-select"
            isMulti
            options={countryOptions}
            value={selectedCountries}
            onChange={setSelectedCountries}
            styles={customSelectStyles}
            placeholder="Select countries to exclude..."
            isDisabled={discoverySettingsLoading || isGenerating}
            isLoading={countryOptions.length === 0}
            closeMenuOnSelect={false}
          />
        </div>

        <div className={styles.rightPanel}>
          <label htmlFor="keywords-textarea" className={styles.panelLabel}>
            Additional Keywords for the AI to filter videos
          </label>
          <p className={styles.panelHelpText}>
            Enter one keyword or phrase per line. Videos must contain at least
            one of these.
          </p>

          {/* NEW AI GENERATOR CONTROLS */}
          <div className={styles.generatorControls}>
            <input
              type="number"
              min="50"
              max="150"
              value={keywordCount}
              onChange={(e) => setKeywordCount(Number(e.target.value))}
              className={styles.countInput}
              disabled={discoverySettingsLoading || isGenerating}
              title="Number of keywords (50-150)"
            />
            <button
              onClick={handleGenerateKeywords}
              className={`${styles.actionButton} ${styles.aiGenerateButton}`}
              disabled={
                discoverySettingsLoading ||
                isGenerating ||
                keywordCount < 50 ||
                keywordCount > 150
              }
            >
              ✨ Auto-Generate
            </button>
          </div>

          <textarea
            className={styles.keywordsTextarea}
            value={keywordsText}
            onChange={(e) => setKeywordsText(e.target.value)}
            // Note: disabled only if we are generating, so user can edit during loading
            disabled={isGenerating}
          />
        </div>
      </div>

      <div className={styles.modalActions}>
        <button
          onClick={handleSave}
          className={`${styles.actionButton} ${styles.saveButton}`}
          disabled={discoverySettingsLoading || isGenerating}
        >
          {discoverySettingsLoading ? 'Saving...' : 'Update Settings'}
        </button>
        <button
          onClick={handleCloseRequest}
          className={`${styles.actionButton} ${styles.closeButton}`}
          disabled={discoverySettingsLoading || isGenerating}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default DiscoverySettingsModal;
